import { Button, Loading, ScalingButtonGroup, Space } from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { PaymentGraphLayout } from '@/features/mold/components';
import { useProject } from '@/features/project/_hooks/project';
import { usePaymentGraph } from '@/features/project/projects/detail/payment/_hooks/payment-graph';
import { usePaymentGroups } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useUpdatePaymentNodesMeta } from '@/features/project/projects/detail/payment/_hooks/payment-node-meta';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from 'reactflow';

export const PaymentGraph: FC = () => {
  const { id } = useId();
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const editPermission = useProjectPermission(true);
  const paymentGroups = usePaymentGroups({ projectId: id });
  const paymentGraph = usePaymentGraph(id);
  const project = useProject(id);
  const { mutateAsync: updatePaymentNodesMetaAsync } =
    useUpdatePaymentNodesMeta();

  if (
    paymentGroups.isPending ||
    !paymentGroups.data ||
    paymentGraph.isPending ||
    !paymentGraph.data ||
    project.isPending ||
    !project.data
  ) {
    return <Loading />;
  }

  return (
    <PaymentGraphLayout
      // NOTES:
      // Necessary for when a new paymentGroup and indirectCost is added on the list tab
      key={
        JSON.stringify(paymentGroups.data) +
        JSON.stringify(project.data.paymentIndirectCosts)
      }
      projectId={id}
      ministryId={project.data.ministryId}
      paymentIndirectCosts={project.data.paymentIndirectCosts}
      paymentGroups={paymentGroups.data}
      paymentNodesMeta={paymentGraph.data.nodesMeta}
      paymentEdges={paymentGraph.data.edges}
      panelNode={(nodes) => (
        <Panel position="top-right">
          <Space direction="horizontal">
            <ScalingButtonGroup />
            <PublishedProjectMutableGuard>
              {editPermission.data && !project.data?.isLocked && (
                <Button
                  onClick={async () => {
                    await updatePaymentNodesMetaAsync({
                      projectId: id,
                      nodesMeta: nodes.map((node) => ({
                        position: {
                          x: node.position.x,
                          y: node.position.y,
                        },
                        nodeId: node.id,
                      })),
                    });
                    await openMessage('success', t('message-update-completed'));
                  }}
                >
                  {t('update-position')}
                </Button>
              )}
            </PublishedProjectMutableGuard>
          </Space>
        </Panel>
      )}
    />
  );
};
